import {css, cx} from '@linaria/core';
import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {COLORS} from '../../../constants/colors';
import {mediaMobileOnly} from '../../../constants/customMedia';
import {overflowEllipsis} from '../../../constants/ellipsis';
import {FONT_SIZES} from '../../../constants/typography';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';

interface SearchSuggestionsProps {
  suggestions: Array<{
    term: React.ReactNode;
    url: string;
  }>;
  className?: string;
  enableRightArrow?: boolean;
  onSuggestionClick?: (suggestionIndex: number) => void;
  leftSearchIcon?: 'search' | 'searchHistory';
  dataTestId?: string;
  highlightedItem?: number | null;
  enableFocusOnTab?: boolean;
}

export const SearchSuggestions: FC<SearchSuggestionsProps> = ({
  suggestions,
  className,
  enableRightArrow,
  onSuggestionClick,
  leftSearchIcon = 'search',
  dataTestId = 'searchSuggestions',
  highlightedItem,
  enableFocusOnTab,
}) => {
  return (
    <div className={className} data-testid={dataTestId}>
      {suggestions.map(({term}, index) => (
        <Suggestion
          key={`search-suggestion-${index}`}
          onClick={() => {
            onSuggestionClick?.(index);
          }}
          data-testid={`${dataTestId}Option${
            highlightedItem === index ? '-selected' : ''
          }`}
          className={cx(highlightedItem === index && HighlightedItemCss)}
          aria-label={undefined}
          tabIndex={enableFocusOnTab ? 0 : -1}
        >
          <InnerWrapper>
            <SearchIcon
              asset={
                leftSearchIcon === 'search'
                  ? GenericIconName.Search
                  : GenericIconName.SearchHistory
              }
              size="XS"
            />
            <TermContainer>{term}</TermContainer>
          </InnerWrapper>
          {enableRightArrow && (
            <Icon asset={GenericIconName.ArrowRight} size="XS" />
          )}
        </Suggestion>
      ))}
    </div>
  );
};

const InnerWrapper = styled.div`
  display: flex;
  flex: 1 1 auto;
  overflow: hidden;
`;

const Suggestion = styled(UnstyledButton)`
  background-color: ${COLORS.White};
  display: flex;
  align-items: flex-start;
  padding: 0.8rem 0.6rem;
  padding-bottom: clamp(0.5rem, 1vh, 0.8rem);
  padding-top: clamp(0.5rem, 1vh, 0.8rem);
  position: relative;
  width: 100%;

  &:focus-visible {
    background-color: ${COLORS['Outline.Grey']};
    outline: none;
  }

  @media (pointer: fine) {
    &:hover {
      background-color: ${COLORS['Outline.Grey']};
    }
  }

  @media ${mediaMobileOnly} {
    padding: 0.625rem 1rem;
  }
`;

const HighlightedItemCss = css`
  && {
    @media (pointer: fine) {
      background-color: ${COLORS['Outline.Grey']};
    }
  }
`;

const SearchIcon = styled(Icon)`
  // the icon needs to be 18x18 pixels to be 1:1 with the design
  // https://www.figma.com/file/78aeShVkIg8jpHIdts5ufT/DT-4747---Search---Auto-Suggest?node-id=968%3A39892&mode=dev
  flex-basis: 18px !important;
  width: 18px !important;
  height: 18px !important;
`;

const TermContainer = styled.div`
  margin: 0 0.5rem;
  font-size: clamp(${FONT_SIZES.px11}, 1vw, ${FONT_SIZES.px14});
  ${overflowEllipsis}

  @media ${mediaMobileOnly} {
    font-size: ${FONT_SIZES.px14};
  }
`;
